import * as React from 'react';

import * as cn from 'classnames';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Autocomplete, CircularProgress, Grid, Link, Spacer, TextField, Tooltip, Typography } from '@emburse/ui-core';
import { Icon, InfoIcon } from '@emburse/ui-essentials';

import { USPSAddressVerificationModal } from '@ReactComponents/PersonalSettings/Components/USPSAddressVerificationModal/USPSAddressVerificationModal';
import { PhoneNumberInput } from '@ReactComponents/PhoneNumberInput/PhoneNumberInput';
import { DateOfBirthField } from '@ReactComponents/Shared/Forms/DateOfBirthField/DateOfBirthField';

import { UpdateUser } from '@Actions/currentUserActions';
import { IAppStateContainer } from '@ReactStore/reducers';

import { UsersApiService } from '@ClientApi/users';
import * as addressHelper from '@Shared/helpers/address';
import { IIsoCountry, IIsoState } from '@Shared/interfaces';
import { IUserAuthenticationMethodModel, IUserModel } from '@Shared/models';

import { SignUpWorkflowFooter } from '../SignUpWorkflowFooter';

import * as style from './SignUpPersonalInfo.styl';

interface ISignUpPersonalInfo {
  onNext: (updatedUser?: IUserModel) => void;
  onBack?: () => void;
  showFlexSpacing?: boolean;
  isSettingsPage?: boolean;
}

export interface IUserForm {
  first_name: string;
  last_name: string;
  street_address: string;
  address_city: string;
  address_state: string;
  postal_code: string;
  address_country: string;
  dob_day: number;
  dob_month: number;
  dob_year: number;
  phone_number: string;
  birthday: Date;
}

export const SignUpPersonalInfo: React.FC<ISignUpPersonalInfo> = ({ onNext, onBack, showFlexSpacing, isSettingsPage }) => {
  const hideAddressAndDOB = (window as any).FEATURES?.svb_optional_user_dob_address === true;
  const user = useSelector((state: IAppStateContainer) => state.currentUser.user);
  const company = useSelector((state: IAppStateContainer) => state.company.company);
  const dispatch = useDispatch();

  let initialBirthday;
  if (user.dob_day && user.dob_month && user.dob_year) {
    initialBirthday = new Date(user.dob_year, user.dob_month - 1, user.dob_day);
  }

  let userState;
  if (user.address_country === 'US') {
    userState = addressHelper.getUSStateNameFromUserStateCodeOrName(user.address_state);
  }

  let phoneNumber = user.phone_number;
  if (phoneNumber && phoneNumber.length === 10 && user.address_country === 'US') {
    phoneNumber = `1${phoneNumber}`;
  }

  const defaultFormValues = {
    first_name: user.first_name,
    last_name: user.last_name,
    street_address: user.street_address || '',
    address_city: user.address_city || '',
    address_state: userState || user.address_state || '',
    postal_code: user.postal_code || '',
    address_country: user.address_country || 'US',
    phone_number: phoneNumber || undefined,
    dob_day: user.dob_day,
    dob_month: user.dob_month,
    dob_year: user.dob_year,
    birthday: initialBirthday
  };
  const userFormMethods = useForm<IUserForm>({
    defaultValues: defaultFormValues
  });
  const countries = addressHelper.getDisplayCountriesAsArray();
  const formCountry = userFormMethods.watch('address_country');
  const selectedCountry = React.useMemo(() => countries.find((country) => country.cldr_display_name === formCountry), [formCountry]);
  const countryTwoLetterCode = addressHelper.getCountryTwoLetterCodeByThreeLetterCode(user.address_country_code);
  const [states, setStates] = React.useState<IIsoState[]>(
    addressHelper.getIsoStatesForCountryByTwoLetterCode(selectedCountry?.iso3166alpha2 || countryTwoLetterCode?.toUpperCase() || 'US')
  );
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [validationErrorMessage, setValidationErrorMessage] = React.useState<string>('');
  const [isFetchingMFA, setIsFetchingMFA] = React.useState<boolean>(false);
  const [mfaMethod, setMfaMethod] = React.useState<IUserAuthenticationMethodModel['method']>();
  const [USPSModalState, setUSPSModalState] = React.useState<any>({
    currentUserInputAddress: {},
    uspsAddressDiff: {},
    uspsReturnText: '',
    isOpen: false
  });

  React.useEffect(() => {
    async function fetchMFA() {
      setIsFetchingMFA(true);
      try {
        const userWithAuth = await UsersApiService.getCurrentUser(['authentication_method', 'mfa_required']);
        setMfaMethod(userWithAuth.authentication_method?.method);
      } finally {
        setIsFetchingMFA(false);
      }
    }

    fetchMFA();
  }, []);

  React.useEffect(() => {
    userFormMethods.reset({
      ...defaultFormValues
    });
  }, [user]);
  const dispatchAndGoNext = (updatedUser: any) => {
    // we have updated user object, update it
    dispatch(new UpdateUser({ ...updatedUser }));
    onNext(updatedUser);
  };

  const onModalAccept = async (addressDiff: any) => {
    const { birthday, ...data } = userFormMethods.getValues();
    const differences: Record<string, string> = Object.keys(addressDiff).reduce((acc: any, key: string) => {
      acc[key] = addressDiff[key].value || addressDiff[key];
      return acc;
    }, {});
    const postData = hideAddressAndDOB
      ? { first_name: data.first_name, last_name: data.last_name, phone_number: data.phone_number }
      : {
          country: selectedCountry?.cldr_display_name,
          address_country_code: selectedCountry?.iso3166alpha3,
          ...data,
          ...differences
        };
    const updatedUser = await UsersApiService.update(user.id, postData);
    setUSPSModalState({ ...USPSModalState, isOpen: false });
    dispatchAndGoNext(updatedUser);
  };

  const openModal = async (userInfo: IUserForm, addressDiff: any, uspsReturnText: string) => {
    setUSPSModalState({
      currentUserInputAddress: userInfo,
      uspsAddressDiff: addressDiff,
      uspsReturnText,
      isOpen: true,
      onAcceptChanges: onModalAccept
    });
  };

  const onSubmit = async (value: IUserForm) => {
    setValidationErrorMessage('');
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const { birthday, ...data } = value;

    const postData = hideAddressAndDOB
      ? { first_name: data.first_name, last_name: data.last_name, phone_number: data.phone_number }
      : {
          ...data,
          country: selectedCountry?.cldr_display_name,
          address_country_code: selectedCountry?.iso3166alpha3,
          dob_day: birthday?.getDate(),
          dob_month: birthday ? birthday.getMonth() + 1 : undefined,
          dob_year: birthday?.getFullYear()
        };

    if (!hideAddressAndDOB && birthday) {
      userFormMethods.setValue('dob_day', birthday.getDate());
      userFormMethods.setValue('dob_month', birthday.getMonth() + 1);
      userFormMethods.setValue('dob_year', birthday.getFullYear());
    }
    try {
      const updatedUser = await UsersApiService.update(user.id, postData);
      const addressDiff = updatedUser.verifyAddressResult?.addressDiff || {};

      if (Object.keys(addressDiff).length) {
        if (addressDiff?.State) {
          // convert 2 letter state code to full state name
          addressDiff.State = addressHelper.getUSStateNameFromUserStateCodeOrName(addressDiff.State);
        }
        openModal(value, addressDiff, updatedUser.verifyAddressResult?.returnText);
      } else {
        dispatchAndGoNext(updatedUser);
      }
    } catch (error: any) {
      let errorMessage;
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage = parsedError.error;
      } catch (e) {
        errorMessage = error.message;
      }
      setValidationErrorMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  function onCountryChange(option: IIsoCountry) {
    const countryStates = addressHelper.getIsoStatesForCountryByTwoLetterCode(option?.iso3166alpha2);
    if (countryStates?.[0]) {
      setStates(countryStates);
      userFormMethods.setValue('address_state', countryStates[0].subdivision_name);
    }
  }

  function renderSpacing(defaultSpacing = 24) {
    if (showFlexSpacing) {
      return (
        <Grid item xs>
          <Spacer size={4} />
        </Grid>
      );
    }
    return <Spacer size={defaultSpacing} />;
  }
  const fieldSpacing = 2;
  const columnSpacing = 3;
  const control = userFormMethods.control;
  const usingSMSMFA = mfaMethod === 'MFA_TEXT';
  return (
    <>
      <FormProvider {...userFormMethods}>
        <Grid container direction="column" spacing={1} sx={{ minHeight: '100%', maxWidth: '500px' }} wrap="nowrap" className={style.global}>
          <Grid
            item
            container
            direction="column"
            spacing={1}
            className={cn(`${company.state === 'pending' && 'info-position'} totalWidth`)}
          >
            <Grid item>
              <Typography variant="h4">Personal Information</Typography>
            </Grid>
            {!isSettingsPage && (
              <Grid item>
                <Typography variant="subtitle1">Tell us about yourself</Typography>
              </Grid>
            )}
            {validationErrorMessage && (
              <Grid item>
                <Alert severity="error" className="alert">
                  {validationErrorMessage}
                </Alert>
              </Grid>
            )}
            <Grid item container direction="column" spacing={columnSpacing} sx={{ marginTop: '0px' }}>
              <Grid item container spacing={fieldSpacing}>
                <Grid item xs>
                  <Controller
                    rules={{ required: 'First name is required' }}
                    control={control}
                    name="first_name"
                    render={({ formState: { errors }, field }) => (
                      <TextField
                        {...field}
                        label="First Name *"
                        fullWidth
                        error={errors.first_name}
                        helperText={errors.first_name?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs>
                  <Controller
                    rules={{ required: 'Last name is required' }}
                    control={control}
                    name="last_name"
                    render={({ formState: { errors }, field }) => (
                      <TextField {...field} label="Last Name *" fullWidth error={errors.last_name} helperText={errors.last_name?.message} />
                    )}
                  />
                </Grid>
              </Grid>
              {isSettingsPage && (
                <Grid item>
                  <TextField fullWidth label="Email" disabled value={user.email} />
                </Grid>
              )}
              {!hideAddressAndDOB && (
                <>
                  <Grid item>
                    <Controller
                      rules={{ required: 'Home Address is required' }}
                      control={control}
                      name="street_address"
                      render={({ formState: { errors }, field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Home Address *"
                          error={errors.street_address}
                          helperText={
                            errors.street_address?.message ||
                            (isSettingsPage ? '' : 'Providing your home address helps us verify your identity.')
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item container spacing={fieldSpacing}>
                    <Grid item xs>
                      <Controller
                        rules={{ required: 'City is required' }}
                        control={control}
                        name="address_city"
                        render={({ formState: { errors }, field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="City *"
                            error={errors.address_city}
                            helperText={errors.address_city?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs>
                      <Controller
                        control={control}
                        name="address_state"
                        rules={{ required: 'State is required' }}
                        render={({ formState: { errors }, field: { onChange, value } }) => {
                          return (
                            <Autocomplete
                              error={Boolean(errors.address_state)}
                              helperText={errors.address_state?.message}
                              autoHighlight
                              autoSelect
                              options={states}
                              fullWidth
                              label="State *"
                              getOptionLabel={(state: any) => state.subdivision_name}
                              value={states.find((option) => option.subdivision_name === value)}
                              onChange={(_e: any, option: IIsoState) => {
                                onChange(option ? option.subdivision_name : '');
                              }}
                            />
                          );
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid item container spacing={fieldSpacing}>
                    <Grid item xs>
                      <Controller
                        rules={{
                          required: 'Zip / Postal Code is required',
                          validate: (value) => {
                            if (selectedCountry?.iso3166alpha2 === 'US') {
                              if (value.length < 5) {
                                return 'Must be at least 5 digits';
                              } else if (value.length > 10) {
                                return 'Must be under 10 digits number';
                              }
                            }
                          }
                        }}
                        control={control}
                        name="postal_code"
                        render={({ formState: { errors }, field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Zip / Postal Code"
                            error={errors.postal_code}
                            helperText={errors.postal_code?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs>
                      <Controller
                        control={control}
                        name="address_country"
                        rules={{ required: 'Country is required' }}
                        render={({ fieldState: { error }, field: { onChange, value } }) => (
                          <Autocomplete
                            error={Boolean(error)}
                            autoHighlight
                            autoSelect
                            options={countries}
                            helperText={error?.message}
                            getOptionLabel={(country: any) =>
                              country.cldr_display_name === 'US' ? 'United States' : country.cldr_display_name
                            }
                            label="Country *"
                            fullWidth
                            value={countries.find((country) => country.cldr_display_name === value)}
                            onChange={(_e: any, option: IIsoCountry) => {
                              onChange(option ? option.cldr_display_name : '');
                              onCountryChange(option);
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <Grid item xs>
                {isFetchingMFA && (
                  <CircularProgress
                    style={{
                      position: 'relative',
                      verticalAlign: 'middle',
                      float: 'right',
                      top: '10px',
                      right: '-28px',
                      marginBottom: '-32px'
                    }}
                    size={22}
                  />
                )}
                {usingSMSMFA && (
                  <Tooltip
                    title={
                      <span>
                        {!isSettingsPage ? (
                          'Your phone number can be changed after completing account setup.'
                        ) : (
                          <>
                            Your account has two-factor authentication enabled. To update your phone number, please visit the{' '}
                            <Link href="/me/security">Passwords page</Link>.
                          </>
                        )}
                      </span>
                    }
                    arrow={true}
                  >
                    <Icon
                      iconUrl={InfoIcon}
                      style={{
                        color: 'var(--brandColor)',
                        position: 'relative',
                        verticalAlign: 'middle',
                        float: 'right',
                        top: '8px',
                        right: '-29px',
                        marginBottom: '-32px'
                      }}
                      sx={{ color: 'warning' }}
                    />
                  </Tooltip>
                )}
                <Controller
                  rules={{
                    required: 'Phone number is required',
                    validate: (value) => {
                      if (selectedCountry?.iso3166alpha2 === 'US') {
                        return value.length > 10 || 'Invalid phone number';
                      }
                    }
                  }}
                  control={control}
                  name="phone_number"
                  render={({ formState: { errors }, field }) => (
                    <PhoneNumberInput
                      disabled={isFetchingMFA || usingSMSMFA}
                      inputProps={{ name: 'phone_number', required: true }}
                      value={field.value}
                      onChange={field.onChange}
                      preferredCountries={['us']}
                      errorMessage={errors?.phone_number?.message}
                    />
                  )}
                />
              </Grid>
              {!hideAddressAndDOB && (
                <Grid item xs>
                  <Controller
                    rules={{ validate: (value: Date) => !isNaN(value?.valueOf()) }}
                    control={control}
                    name="birthday"
                    render={({ formState: { errors }, field }) => (
                      <DateOfBirthField
                        {...field}
                        label={'Date of Birth *'}
                        helperText={isSettingsPage ? '' : 'Providing your birthday helps us verify your identity.'}
                        error={errors.birthday}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          {renderSpacing()}
          <Grid item alignContent={showFlexSpacing ? 'flex-end' : 'flex-start'}>
            <SignUpWorkflowFooter
              onBack={onBack}
              onNext={userFormMethods.handleSubmit(onSubmit)}
              showSupportLink={!isSettingsPage}
              isSubmitting={isSubmitting}
              ariaLabel="personal-info"
            />
          </Grid>
        </Grid>
      </FormProvider>
      {Boolean(USPSModalState.isOpen) && (
        <USPSAddressVerificationModal
          currentUserInputAddress={USPSModalState.currentUserInputAddress}
          uspsReturnText={USPSModalState.uspsReturnText}
          uspsAddressDiff={USPSModalState.uspsAddressDiff}
          isOpen={USPSModalState.isOpen}
          onClose={() => setUSPSModalState({ ...USPSModalState, isOpen: false })}
          onAcceptChanges={USPSModalState.onAcceptChanges}
        />
      )}
    </>
  );
};
